import { GRID_SIZE, GridCellLayoutVariant, defaultLayouts } from "@/constants/gridCellLayouts";
import type { GenericLayout, GenericLayoutCreate, GridCellLayout, Tab } from "@mm/metx-workbench.meteomatics.com";
import { LayoutType } from "@mm/metx-workbench.meteomatics.com";

type ToolLayoutReference = {
  id: number;
  type: GenericLayoutCreate["type"];
};

type TabWithLayoutsAndTools = Pick<
  Tab,
  | "layouts"
  | "maps"
  | "plots"
  | "energy_plots"
  | "weather_tables"
  | "location_tables"
  | "tephigrams"
  | "country_plots"
  | "notes"
>;

const exactDefaultLayoutByToolCount: Partial<Record<number, GridCellLayoutVariant>> = {
  1: GridCellLayoutVariant.R1C1,
  2: GridCellLayoutVariant.R1C2,
  3: GridCellLayoutVariant.R1C3,
  4: GridCellLayoutVariant.R2C2,
  6: GridCellLayoutVariant.R2C3,
};

const getTabToolLayoutReferences = (tab: TabWithLayoutsAndTools): ToolLayoutReference[] => [
  ...(tab.maps ?? []).map((tool) => ({ id: tool.id, type: LayoutType.Map })),
  ...(tab.plots ?? []).map((tool) => ({ id: tool.id, type: LayoutType.Plot })),
  ...(tab.energy_plots ?? []).map((tool) => ({ id: tool.id, type: LayoutType.EnergyPlot })),
  ...(tab.weather_tables ?? []).map((tool) => ({ id: tool.id, type: LayoutType.WeatherTable })),
  ...(tab.location_tables ?? []).map((tool) => ({ id: tool.id, type: LayoutType.LocationTable })),
  ...(tab.tephigrams ?? []).map((tool) => ({ id: tool.id, type: LayoutType.Tephigram })),
  ...(tab.country_plots ?? []).map((tool) => ({ id: tool.id, type: LayoutType.CountryPlot })),
  ...(tab.notes ?? []).map((tool) => ({ id: tool.id, type: LayoutType.Notes })),
];

const createBalancedGridCellLayouts = (count: number): GridCellLayout[] => {
  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);

  return Array.from({ length: count }, (_, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);

    return {
      gridColumnStart: Math.floor((column * GRID_SIZE) / columns) + 1,
      gridColumnEnd: Math.floor(((column + 1) * GRID_SIZE) / columns) + 1,
      gridRowStart: Math.floor((row * GRID_SIZE) / rows) + 1,
      gridRowEnd: Math.floor(((row + 1) * GRID_SIZE) / rows) + 1,
    };
  });
};

const getGridCellLayoutsForToolCount = (count: number): GridCellLayout[] => {
  const defaultLayoutVariant = exactDefaultLayoutByToolCount[count];
  if (defaultLayoutVariant !== undefined) {
    return defaultLayouts[defaultLayoutVariant].gridCellLayouts;
  }

  return createBalancedGridCellLayouts(count);
};

export const hasToolsWithoutLayouts = (tab: TabWithLayoutsAndTools): boolean =>
  (tab.layouts ?? []).length === 0 && getTabToolLayoutReferences(tab).length > 0;

export const buildFallbackLayoutsForTab = (tab: TabWithLayoutsAndTools): GenericLayoutCreate[] | null => {
  if ((tab.layouts ?? []).length > 0) {
    return null;
  }

  const tools = getTabToolLayoutReferences(tab);
  if (tools.length === 0) {
    return [
      {
        gridCellLayout: defaultLayouts[GridCellLayoutVariant.R1C1].gridCellLayouts[0],
        type: LayoutType.Undefined,
        id_tool: null,
      },
    ];
  }

  const gridCellLayouts = getGridCellLayoutsForToolCount(tools.length);
  return tools.map((tool, index) => ({
    gridCellLayout: gridCellLayouts[index],
    type: tool.type,
    id_tool: tool.id,
  }));
};

export const buildTransientFallbackLayoutsForTab = (tab: TabWithLayoutsAndTools & { id: number }): GenericLayout[] => {
  const fallbackLayouts = buildFallbackLayoutsForTab(tab) ?? [];

  return fallbackLayouts.map((layout, index) => ({
    ...layout,
    id: -(index + 1),
    id_tab: tab.id,
  }));
};

export const hasTransientFallbackLayouts = (layouts: Array<Pick<GenericLayout, "id">>): boolean =>
  layouts.some((layout) => layout.id < 0);
